import React from 'react';

// "Game" tab: a read-only window into the actual game, fed by the sync
// script from the real objectives JSON + planet data (project.game).
// Endgame content is stripped before it ever reaches the database.

const BIOME_COLORS = {
  lush: '#7FB069',
  icy: '#9FC3D4',
  rocky: '#AAB5A8',
  sandy: '#C9A45C',
  volcanic: '#FF8442'
};

const biomeColor = (biome) => {
  const key = String(biome || '').toLowerCase();
  for (const [b, c] of Object.entries(BIOME_COLORS)) {
    if (key.includes(b)) return c;
  }
  return '#9A9FA5';
};

const STATUS_LABELS = {
  built: { label: 'In game', color: '#4CAF50' },
  building: { label: 'Being built', color: '#3BA6FF' },
  planned: { label: 'Planned', color: '#9A9FA5' }
};

const PHASE_TITLES = {
  1: 'Part 1 - Planet 1',
  2: 'Part 2 - Planets 2-?',
  3: 'Part 3 - The ship'
};

function ObjectiveRow({ o }) {
  return (
    <div className={`pv-game-obj ${o.wired ? 'wired' : 'planned'}`}>
      <span className="material-symbols-outlined pv-game-obj-icon">
        {o.wired ? 'task_alt' : 'pending'}
      </span>
      <div className="pv-game-obj-text">
        <span className="pv-game-obj-title">
          {o.title}
          {o.need ? <span className="pv-game-obj-need">x{o.need}{o.item ? ` ${o.item}` : ''}</span> : null}
        </span>
        {o.wired && o.hint && <span className="pv-game-obj-hint">{o.hint}</span>}
      </div>
      {!o.wired && <span className="pv-game-obj-planned">planned</span>}
    </div>
  );
}

function ProjectGameView({ project }) {
  const game = project.game || {};
  const planets = game.planets || [];
  const objectives = game.objectives || [];
  const systems = game.systems || [];
  const stats = game.stats || {};
  const [lightbox, setLightbox] = React.useState(null);

  const mains = objectives.filter(o => o.track === 'main');
  const sides = objectives.filter(o => o.track === 'side');
  const phases = [...new Set(mains.map(o => o.phase))].sort((a, b) => a - b);

  if (!objectives.length && !planets.length) {
    return (
      <div className="pv-empty-files">
        <span className="material-symbols-outlined">public</span>
        <p>No game data yet</p>
      </div>
    );
  }

  const statBits = [];
  if (stats.objectivesTotal) statBits.push(`${stats.objectivesWired}/${stats.objectivesTotal} objectives playable`);
  if (stats.planetsTotal) statBits.push(`${stats.planetsBuilt}/${stats.planetsTotal} worlds built`);
  if (stats.assets) statBits.push(`${stats.assets.wired}/${stats.assets.total} models in game`);

  return (
    <div className="pv-game">
      {statBits.length > 0 && (
        <div className="pv-game-stats">{statBits.join('  ·  ')}</div>
      )}

      {(game.recent || []).length > 0 && (
        <div className="pv-game-section">
          <div className="pv-overview-card-title">
            <span className="material-symbols-outlined">history</span>
            Recently worked on
          </div>
          {game.recent.map((r, i) => (
            <div key={i} className="pv-game-recent-row">
              <span className="pv-game-recent-date">
                {new Date(r.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </span>
              <span className="pv-game-recent-title">{r.title}</span>
            </div>
          ))}
        </div>
      )}

      {planets.length > 0 && (
        <div className="pv-game-section">
          <div className="pv-overview-card-title">
            <span className="material-symbols-outlined">travel_explore</span>
            Worlds
          </div>
          <div className="pv-game-map">
            {planets.map((p, idx) => {
              const c = biomeColor(p.biome);
              const status = STATUS_LABELS[p.status];
              const isPlanned = p.status === 'planned';
              return (
                <React.Fragment key={p.key}>
                  {idx > 0 && (
                    <span className="material-symbols-outlined pv-game-map-arrow">arrow_forward</span>
                  )}
                  <div className={`pv-game-world ${isPlanned ? 'planned' : ''}`}>
                    <div className="pv-game-world-header">
                      <span className="pv-game-planet-order" style={{ background: c }}>{p.order}</span>
                      <span className="pv-game-world-name">{p.name}</span>
                    </div>
                    {status && (
                      <span className="pv-game-status" style={{ color: status.color }}>{status.label}</span>
                    )}
                    <div className="pv-game-planet-chips">
                      <span className="pv-game-chip" style={{ color: c, borderColor: c }}>{p.biome}</span>
                      <span className="pv-game-chip">air: {p.air}</span>
                      <span className="pv-game-chip">{p.shield}</span>
                    </div>
                    {!isPlanned && (
                      <>
                        <p className="pv-game-planet-blurb">{p.blurb}</p>
                        {p.features?.length > 0 && (
                          <ul className="pv-game-planet-features">
                            {p.features.map((f, i) => <li key={i}>{f}</li>)}
                          </ul>
                        )}
                      </>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
            <span className="material-symbols-outlined pv-game-map-arrow">arrow_forward</span>
            <div className="pv-game-world teaser">
              <span className="material-symbols-outlined">more_horiz</span>
              <p>{game.centerTeaser || 'More worlds ahead.'}</p>
            </div>
          </div>
        </div>
      )}

      {systems.length > 0 && (
        <div className="pv-game-section">
          <div className="pv-overview-card-title">
            <span className="material-symbols-outlined">memory</span>
            Systems
          </div>
          <div className="pv-game-systems">
            {systems.map(s => (
              <div key={s.key} className="pv-game-system">
                {s.imageUrl && (
                  <div className="pv-game-system-img" onClick={() => setLightbox(s.imageUrl)}>
                    <img src={s.imageUrl} alt={s.name} loading="lazy" decoding="async" />
                  </div>
                )}
                <div className="pv-game-system-body">
                  <span className="pv-game-system-name">{s.name}</span>
                  <ul className="pv-game-system-facts">
                    {(s.facts || []).map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {mains.length > 0 && (
        <div className="pv-game-section">
          <div className="pv-overview-card-title">
            <span className="material-symbols-outlined">route</span>
            Objectives
          </div>
          {phases.map(phase => (
            <div key={phase} className="pv-game-phase">
              <div className="pv-game-phase-title">{PHASE_TITLES[phase] || `Part ${phase}`}</div>
              {mains.filter(o => o.phase === phase).map(o => <ObjectiveRow key={o.id} o={o} />)}
            </div>
          ))}

          {sides.length > 0 && (
            <div className="pv-game-phase">
              <div className="pv-game-phase-title">Side objectives</div>
              {sides.map(o => <ObjectiveRow key={o.id} o={o} />)}
            </div>
          )}

          {game.endgameHidden && (
            <div className="pv-game-endgame-note">
              <span className="material-symbols-outlined">visibility_off</span>
              Endgame hidden
            </div>
          )}
        </div>
      )}

      {game.updatedAt && (
        <div className="pv-overview-updated">
          Game data synced {new Date(game.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
        </div>
      )}

      {lightbox && (
        <div className="pv-task-lightbox" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" />
        </div>
      )}
    </div>
  );
}

export default ProjectGameView;
